import { useCallback } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import {
  useBoardSelectionStore,
  useBoardViewportStore,
  useWallContentStore,
  useWallStore,
  type CardId,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { findCardById } from '../../card-actions/utils/cardLookup';
import { getNewCardPlacement } from '../../card-actions/utils/cardPlacement';
import { sortBoardZIndexes } from '../../positioning/zIndex';

interface BoardPoint {
  x: number;
  y: number;
}

export function useCardCommands(board: WallBoard) {
  const addCard = useWallContentStore((state) => state.addCard);
  const addCards = useWallContentStore((state) => state.addCards);
  const cards = useWallContentStore((state) => state.cards);
  const shapes = useWallContentStore((state) => state.shapes);
  const moveCardToCluster = useWallContentStore((state) => state.moveCardToCluster);
  const removeCardFromStore = useWallContentStore((state) => state.removeCard);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const updateCards = useWallContentStore((state) => state.updateCards);
  const updateCardZIndexes = useWallContentStore((state) => state.updateCardZIndexes);
  const updateShapeZIndexes = useWallContentStore((state) => state.updateShapeZIndexes);
  const currentElement = useBoardSelectionStore((state) => state.currentElement);
  const selectedIds = useBoardSelectionStore((state) => state.selectedIds);
  const clearSelection = useBoardSelectionStore((state) => state.clearSelection);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const dropPosition = useBoardViewportStore((state) => state.dropPosition);
  const topicKey = useWallStore((state) => state.topicKey);

  const getPlacement = useCallback(
    (index = 0, point?: BoardPoint | null) => {
      const currentCard =
        currentElement?.type === 'card' || currentElement?.type === 'cluster'
          ? findCardById(cards, currentElement.id)
          : null;

      if (point) {
        return {
          xPos: point.x + index * 15,
          yPos: point.y + index * 15,
        };
      }

      if (currentCard && currentCard.xPos != null && currentCard.yPos != null) {
        return {
          xPos: currentCard.xPos,
          yPos: currentCard.yPos + (currentCard.height ?? 80) + 5 + index * 15,
        };
      }

      const placement = getNewCardPlacement({ dropPosition, existingCards: cards });

      return {
        xPos: placement.xPos + index * 15,
        yPos: placement.yPos + index * 15,
      };
    },
    [cards, currentElement, dropPosition]
  );

  const promoteCardsToFront = useCallback(
    (placedCards: WallCard[], baseCards = cards) => {
      const placedCardIds = placedCards.map((card) => card.id);
      const orderedPlacedCards = placedCards.map((card, index) => ({ ...card, zPos: index }));
      const nextCards = [
        ...baseCards.filter((card) => !placedCardIds.includes(card.id)),
        ...orderedPlacedCards,
      ];
      const sortedZIndexes = sortBoardZIndexes(nextCards, shapes, placedCardIds);

      updateCardZIndexes(sortedZIndexes.cards);
      updateShapeZIndexes(sortedZIndexes.shapes);

      void Promise.all([
        sortedZIndexes.cards.length > 0
          ? wallsApi.updateCardZIndexes(board.id, sortedZIndexes.cards, topicKey)
          : Promise.resolve(),
        sortedZIndexes.shapes.length > 0
          ? wallsApi.updateShapeZIndexes(board.id, sortedZIndexes.shapes, topicKey)
          : Promise.resolve(),
      ]);

      return placedCards.map((card) => {
        const zIndex = sortedZIndexes.cards.find(
          (currentZIndex) => currentZIndex.cardId === card.id
        );

        return zIndex ? { ...card, zPos: zIndex.zPos } : card;
      });
    },
    [board.id, cards, shapes, topicKey, updateCardZIndexes, updateShapeZIndexes]
  );

  const placeCard = useCallback(
    (card: WallCard, index = 0, point?: BoardPoint | null) => {
      const placement = getPlacement(index, point);
      const placedCard = { ...card, ...placement };

      addCard(placedCard);
      const [promotedCard] = promoteCardsToFront([placedCard]);
      setCurrentElement({
        id: promotedCard.id,
        type: promotedCard.persistCluster ? 'cluster' : 'card',
      });
      void wallsApi.updateCardPosition(
        board.id,
        promotedCard.id,
        { id: promotedCard.id, xPos: placement.xPos, yPos: placement.yPos },
        topicKey
      );
      publishBoardSocketPackage({
        action: 'topicCardAddedToBoard',
        topic: board.id,
        card: { ...promotedCard, boardId: board.id },
      });

      return promotedCard;
    },
    [addCard, board.id, getPlacement, promoteCardsToFront, setCurrentElement, topicKey]
  );

  const placeCardInCluster = useCallback(
    async (card: WallCard, targetClusterId: CardId) => {
      const cluster = findCardById(cards, targetClusterId);

      if (!cluster) {
        return placeCard(card);
      }

      const zPos =
        cluster.cards.reduce(
          (highestZPos, childCard) => Math.max(highestZPos, childCard.zPos ?? 0),
          0
        ) + 1;

      addCard(card);
      moveCardToCluster(card.id, cluster.id, zPos);
      setCurrentElement({ id: card.id, type: 'card' });
      await wallsApi.addCardToCluster(board.id, cluster.id, card.id, zPos, topicKey);
      publishBoardSocketPackage({
        action: 'topicCardAddedToBoard',
        topic: board.id,
        card: { ...card, boardId: board.id },
      });
      publishBoardSocketPackage({
        action: 'addCardToCluster',
        topic: board.id,
        updateDetail: { cardId: card.id, clusterId: cluster.id, zPos },
      });

      return { ...card, parentId: cluster.id, zPos };
    },
    [addCard, board.id, cards, moveCardToCluster, placeCard, setCurrentElement, topicKey]
  );

  const createCards = useCallback(
    (createdCards: WallCard[], point?: BoardPoint | null) => {
      const placedCards = createdCards.map((card, index) => ({
        ...card,
        ...getPlacement(index, point),
      }));

      addCards(placedCards);
      const promotedCards = promoteCardsToFront(placedCards);

      promotedCards.forEach((card) => {
        if (card.xPos != null && card.yPos != null) {
          void wallsApi.updateCardPosition(
            board.id,
            card.id,
            { id: card.id, xPos: card.xPos, yPos: card.yPos },
            topicKey
          );
        }
        publishBoardSocketPackage({
          action: 'topicCardAddedToBoard',
          topic: board.id,
          card: { ...card, boardId: board.id },
        });
      });

      if (placedCards[0]) {
        setCurrentElement({
          id: promotedCards[0].id,
          type: promotedCards[0].persistCluster ? 'cluster' : 'card',
        });
      }

      return promotedCards;
    },
    [addCards, board.id, getPlacement, promoteCardsToFront, setCurrentElement, topicKey]
  );

  return {
    createCards,

    createCardsAt: useCallback(
      (createdCards: WallCard[], point: BoardPoint) => createCards(createdCards, point),
      [createCards]
    ),

    createTextCard: useCallback(
      async (shape: string, content: string, color: string, targetClusterId?: CardId | null) => {
        const card = await wallsApi.insertTextCard(board.id, shape, content, color, topicKey);

        if (targetClusterId) {
          return placeCardInCluster(card, targetClusterId);
        }

        return placeCard(card);
      },
      [board.id, placeCard, placeCardInCluster, topicKey]
    ),

    deleteCard: useCallback(
      async (cardId: CardId) => {
        await wallsApi.deleteCard(board.id, cardId, topicKey);
        removeCardFromStore(cardId);
        clearSelection();
        publishBoardSocketPackage({
          action: 'boardCardDeleted',
          topic: board.id,
          card: { id: cardId },
        });
      },
      [board.id, clearSelection, removeCardFromStore, topicKey]
    ),

    convertCardToCluster: useCallback(
      async (cardId: CardId) => {
        updateCard(cardId, { persistCluster: true });
        setCurrentElement({ id: cardId, type: 'cluster' });
        const cluster = await wallsApi.convertCardToCluster(board.id, cardId, topicKey);
        publishBoardSocketPackage({
          action: 'topicClusterUpdated',
          topic: board.id,
          cluster: { ...cluster, boardId: board.id },
        });
      },
      [board.id, setCurrentElement, topicKey, updateCard]
    ),

    duplicateCard: useCallback(
      async (card: WallCard) => {
        const duplicatedCard = await wallsApi.duplicateCard(board.id, card.id, topicKey);

        addCard(duplicatedCard);
        let placedDuplicatedCard = duplicatedCard;

        if (duplicatedCard.parentId) {
          const cluster = findCardById(cards, duplicatedCard.parentId);
          const zPos =
            cluster?.cards.reduce(
              (highestZPos, childCard) => Math.max(highestZPos, childCard.zPos ?? 0),
              0
            ) ??
            duplicatedCard.zPos ??
            card.zPos ??
            1;
          const nextZPos = zPos + 1;

          placedDuplicatedCard = { ...duplicatedCard, zPos: nextZPos };
          moveCardToCluster(duplicatedCard.id, duplicatedCard.parentId, nextZPos);
          void wallsApi.addCardToCluster(
            board.id,
            duplicatedCard.parentId,
            duplicatedCard.id,
            nextZPos,
            topicKey
          );
        } else {
          placedDuplicatedCard = promoteCardsToFront([duplicatedCard])[0];
        }

        setCurrentElement({
          id: placedDuplicatedCard.id,
          type:
            placedDuplicatedCard.persistCluster || placedDuplicatedCard.cards?.length
              ? 'cluster'
              : 'card',
        });
        publishBoardSocketPackage({
          action: 'topicCardAddedToBoard',
          topic: board.id,
          card: { ...placedDuplicatedCard, boardId: board.id },
        });

        return placedDuplicatedCard;
      },
      [
        addCard,
        board.id,
        cards,
        moveCardToCluster,
        promoteCardsToFront,
        setCurrentElement,
        topicKey,
      ]
    ),

    lockCard: useCallback(
      async (cardId: CardId) => {
        updateCard(cardId, { isLocked: true });
        clearSelection();
        await wallsApi.lockCard(board.id, cardId, topicKey);
        publishBoardSocketPackage({
          action: 'lockCard',
          topic: board.id,
          card: { id: cardId },
        });
      },
      [board.id, clearSelection, topicKey, updateCard]
    ),

    resetCardSize: useCallback(
      async (cardId: CardId) => {
        updateCard(cardId, { height: undefined, width: undefined });
        await wallsApi.updateCardSize(
          board.id,
          cardId,
          { height: null, id: cardId, width: null },
          topicKey
        );
        publishBoardSocketPackage({
          action: 'undoCardResize',
          topic: board.id,
          size: { id: cardId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    setCardColor: useCallback(
      async (cardId: CardId, color: string) => {
        updateCard(cardId, { color });
        await wallsApi.setCardColor(board.id, [cardId], color, topicKey);
        publishBoardSocketPackage({
          action: 'cardColorsChanged',
          topic: board.id,
          cardIds: [cardId],
          color,
        });
      },
      [board.id, topicKey, updateCard]
    ),

    setSelectedCardsColor: useCallback(
      async (color: string) => {
        if (selectedIds.length === 0) {
          return;
        }

        const cardIds = selectedIds.filter((selectedId) => findCardById(cards, selectedId));

        if (cardIds.length === 0) {
          return;
        }

        updateCards(cardIds, { color });
        await wallsApi.setCardColor(board.id, cardIds, color, topicKey);
        publishBoardSocketPackage({
          action: 'cardColorsChanged',
          topic: board.id,
          cardIds,
          color,
        });
      },
      [board.id, cards, selectedIds, topicKey, updateCards]
    ),

    updateImageCard: useCallback(
      async (cardId: CardId, title: string, color: string) => {
        await wallsApi.updateImageCard(board.id, cardId, title, color, topicKey);
        updateCard(cardId, { color, title });
        publishBoardSocketPackage({
          action: 'topicCardUpdated',
          topic: board.id,
          card: { boardId: board.id, color, id: cardId, title },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    updateTextCard: useCallback(
      async (cardId: CardId, content: string, color: string) => {
        await wallsApi.updateTextCard(board.id, cardId, content, color, topicKey);
        updateCard(cardId, { color, content });
        publishBoardSocketPackage({
          action: 'topicCardUpdated',
          topic: board.id,
          card: { boardId: board.id, color, content, id: cardId },
        });
      },
      [board.id, topicKey, updateCard]
    ),

    unlockCard: useCallback(
      async (cardId: CardId) => {
        updateCard(cardId, { isLocked: false });
        await wallsApi.unlockCard(board.id, cardId, topicKey);
        publishBoardSocketPackage({
          action: 'unlockCard',
          topic: board.id,
          card: { id: cardId },
        });
      },
      [board.id, topicKey, updateCard]
    ),
  };
}
